import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { CARD_TYPES, ICON_TYPES } from '../../../shared/constants';
import { IContentfulLink, IImage } from '../../../types/shared';

import PrimaryButton from '../../atoms/primary-button/PrimaryButton';

import { DEQ_TYPES } from '../../../utils/deq';
import { getCtaLink } from '../../../utils/generic';

import mq from '../../../styles/breakpoints';
import { Body, H1SemiBoldCss, H3, IntroText } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

interface Props {
  title?: string;
  image?: IImage;
  children?: ReactNode;
  primaryLink?: IContentfulLink;
  uspType: string;
}

const UspItem = ({ title, image, children, primaryLink, uspType }: Props) => (
  <Container
    data-testid="usp-item"
    id={uspType}
    lessMargin={uspType === CARD_TYPES.uspNumerical}
  >
    {image && uspType !== CARD_TYPES.uspNumerical && (
      <ImageContainer>
        <img alt={image?.description || 'usp-image'} src={image.file?.url} />
      </ImageContainer>
    )}
    {!image && uspType === CARD_TYPES.uspNumerical && (
      <>
        {title && <NumericalTitle>{title}</NumericalTitle>}
        {children && <StyledIntroText>{children}</StyledIntroText>}
      </>
    )}
    {uspType === CARD_TYPES.usp && (
      <>
        {title && <StyledH3>{title}</StyledH3>}
        {children && <StyledBody>{children}</StyledBody>}
      </>
    )}
    {primaryLink && (
      <ButtonWrapper>
        <PrimaryButton
          buttonTheme={primaryLink.theme}
          to={getCtaLink(primaryLink)}
          deqComponentTitle={primaryLink?.label}
          type={primaryLink.type}
          icon={ICON_TYPES.none}
          label={primaryLink?.label}
          deqCustomType="alinea-ankeiler"
          deqCustomAttributes={{
            compType: 'alinea-ankeiler',
            elementType: DEQ_TYPES.button,
            elementTarget: getCtaLink(primaryLink),
          }}
        />
      </ButtonWrapper>
    )}
  </Container>
);

export default UspItem;

const Container = styled.div<{ lessMargin?: boolean }>`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0;
  text-align: center;
  width: 100%;

  ${mq.from.breakpoint.M`
    flex-wrap: wrap;
    width: auto;
    max-width: 310px;
  `}

  ${mq.from.breakpoint.L`
    flex-wrap: wrap;
    max-width: 408px;
  `}
`;

const ImageContainer = styled.div`
  display: flex;
  height: 110px;
  justify-items: center;
  margin-bottom: ${Spacings.XS};

  img {
    height: inherit;
  }

  ${mq.from.breakpoint.L`
    margin-bottom: ${Spacings.M};
  `}
`;

const StyledH3 = styled(H3)`
  margin: 0;
`;

const NumericalTitle = styled.h3`
  ${H1SemiBoldCss};
  color: ${({ theme }) => theme.colors.abnGreen600};
`;

const StyledIntroText = styled(IntroText)`
  > p:first-of-type {
    margin: 0;
  }
  > p {
    margin: ${Spacings.XS} 0 0 0;
  }
`;

const StyledBody = styled(Body)`
  flex: 1;
  margin-top: 8px;
`;

const ButtonWrapper = styled.div`
  justify-content: flex-end;
  margin-top: 32px;
`;
